// src/components/Header.jsx
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { FaBell, FaSpinner } from "react-icons/fa";
import { FiEye, FiMenu, FiMoon, FiPaperclip, FiSun } from "react-icons/fi";
import { BASE_URL } from "../utils";
import { toast } from "react-toastify";
import { useMode } from "../contexts/ModeContext";
import { useTheme } from "../contexts/ThemeContext";
import ModeDropdown from "./ModeDropdown";

export default function Header({ onMenuToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useMode();
  const { theme, toggleTheme } = useTheme();

  const [showDropdown, setShowDropdown] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [loadingIds, setLoadingIds] = useState([]);
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // portal positioning
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0, width: 420 });

  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch {
      return html.replace(/<[^>]+>/g, "");
    }
  };

  const isComplexHtml = (html) => {
    if (!html) return false;
    const lowered = ("" + html).toLowerCase();
    const tagCount = (html.match(/<\w+/g) || []).length;
    if (tagCount > 10) return true;
    if (lowered.includes("startfragment")) return true;
    if (
      lowered.includes("<div") ||
      lowered.includes("<table") ||
      lowered.includes("<img")
    )
      return true;
    if (html.length > 800) return true;
    return false;
  };

  const sanitizeHtmlForView = (html) => {
    if (!html) return "";
    let s = html.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
    s = s.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    return s;
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(sanitizeHtmlForView(html || ""));
    setHtmlModalOpen(true);
  };

  const buildPdfHref = (pdfUrl) => {
    if (!pdfUrl) return null;
    if (pdfUrl.startsWith("http://") || pdfUrl.startsWith("https://"))
      return pdfUrl;
    const path = pdfUrl.startsWith("/") ? pdfUrl : `/${pdfUrl}`;
    return `${BASE_URL}${path}`;
  };

  const fetchFollowUps = async (autoOpen = false) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/case/today`);
      const data = Array.isArray(res.data) ? res.data : [];
      setFollowUps(data);
      if (autoOpen && data.length > 0) setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching follow-ups", err);
    }
  };

  const markAsDone = async (caseId) => {
    if (!caseId) return;
    setLoadingIds((prev) => [...prev, caseId]);
    try {
      await axios.post(`${BASE_URL}/api/case/mark-read`, { case_id: caseId });
      setFollowUps((prev) => prev.filter((f) => f.case_id !== caseId));
      toast.success("Marked as done");
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      console.error("Error updating read status", err);
      toast.error("Failed to mark as done.");
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== caseId));
    }
  };

  useEffect(() => {
    let lastTrigger = null;
    fetchFollowUps();

    const checkSchedule = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      if ((h === 11 || h === 16) && m === 0) {
        const key = `${h}:${m}`;
        if (lastTrigger !== key) {
          lastTrigger = key;
          fetchFollowUps(true);
        }
      }
    };

    const id = setInterval(checkSchedule, 60000);
    return () => clearInterval(id);
  }, []);

  // compute portal dropdown position (fixed)
  const computeMenuPos = () => {
    const btn = btnRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const gap = 10;

    // desired width
    const desired = Math.min(420, Math.floor(window.innerWidth * 0.9));
    const right = Math.max(8, window.innerWidth - rect.right);
    const top = rect.bottom + gap;

    setMenuPos({ top, right, width: desired });
  };

  useLayoutEffect(() => {
    if (!showDropdown) return;
    computeMenuPos();

    const onScroll = () => computeMenuPos();
    const onResize = () => computeMenuPos();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [showDropdown]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        showDropdown &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setShowDropdown(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!mode) return;

    const pathname = location.pathname || "/";
    const targetPrefix =
      mode === "legacy"
        ? "/legacy"
        : mode === "acris-legacy"
          ? "/acris-legacy"
          : mode === "violation"
            ? "/violations"
            : mode === "violations-v2"
              ? "/violations-v2"
              : "/websurrogate";

    if (pathname.startsWith(targetPrefix)) return;

    const otherPrefixes = ["/legacy", "/acris-legacy", "/websurrogate", "/violations", "/violations-v2"].filter(
      (p) => p !== targetPrefix,
    );

    if (otherPrefixes.some((p) => pathname.startsWith(p))) {
      if (mode === "violation") {
        navigate("/violations/dashboard", { replace: true });
        return;
      }
      if (mode === "violations-v2") {
        navigate("/violations-v2/dashboard", { replace: true });
        return;
      }
      if (mode === "acris-legacy") {
        navigate("/acris-legacy/dashboard", { replace: true });
        return;
      }

      if (pathname.startsWith("/violations")) {
        navigate(`${targetPrefix}/dashboard`, { replace: true });
        return;
      }

      const oppositePrefix = mode === "legacy" ? "/websurrogate" : "/legacy";
      if (pathname.startsWith(oppositePrefix)) {
        const rest = pathname.slice(oppositePrefix.length) || "/dashboard";
        const target = `${targetPrefix}${rest}`;
        if (target !== pathname) navigate(target, { replace: true });
        return;
      }
    }

    const preserveRootDeepLinks = false;
    if (
      preserveRootDeepLinks &&
      pathname.startsWith("/case-details") &&
      mode !== "legacy"
    ) {
      return;
    }

    const rootLike =
      pathname === "/" || pathname === "" || pathname === "/dashboard";
    if (mode === "violation") {
      if (rootLike) {
        navigate("/violations/dashboard", { replace: true });
      }
      return;
    }
    if (mode === "violations-v2") {
      if (rootLike) {
        navigate("/violations-v2/dashboard", { replace: true });
      }
      return;
    }
    if (mode === "acris-legacy") {
      if (rootLike) {
        navigate("/acris-legacy/dashboard", { replace: true });
      }
      return;
    }

    if (rootLike) {
      navigate(`${targetPrefix}/dashboard`, { replace: true });
      return;
    }

    const prefixed = `${targetPrefix}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
    if (prefixed !== pathname) {
      navigate(prefixed, { replace: true });
    }
  }, [mode, location.pathname, navigate]);

  const buildCaseHref = (item) => {
    const id = item?.case_id ?? "";
    const court = item?.petition_court_type ?? item?.petition_court ?? "";
    const type =
      item?.petition_processing_file_type ?? item?.petition_type ?? "";
    const prefix = mode === "legacy" ? "/legacy" : "/websurrogate";

    if (!id) return `${prefix}/case-details/unknown`;
    if (!court && !type)
      return `${prefix}/case-details/${encodeURIComponent(id)}`;

    const enc = (...parts) =>
      parts.map((p) => encodeURIComponent(String(p))).join("/");
    return `${prefix}/case-details/${enc(id, court || "", type || "")}`;
  };

  const handleCaseClick = (event, href) => {
    if (mode !== "webSurrogate" && mode !== "websurrogate" && mode !== "web") {
      event.preventDefault();
      window.alert("You are not in WebSurrogate mode. Please switch to open case details.");
      return;
    }
    if (!href) event.preventDefault();
  };

  async function copyToClipboard(
    text,
    successMsg = "Copied",
    failMsg = "Copy failed",
  ) {
    const payload = text == null ? "" : String(text);

    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(successMsg);
        return true;
      } catch {}
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = payload;
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.width = "1px";
      textarea.style.height = "1px";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (ok) {
        toast.success(successMsg);
        return true;
      } else {
        toast.error(failMsg);
        return false;
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(failMsg);
      return false;
    }
  }

  // dropdown rendered in portal so it always overlays everything
  const dropdownNode =
    showDropdown &&
    ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="fixed rounded-2xl shadow-2xl overflow-hidden border border-[var(--border)] bg-[var(--surface)]"
        style={{
          top: menuPos.top,
          right: menuPos.right,
          width: menuPos.width,
          zIndex: 999999,
        }}
      >
        <div className="px-4 py-3 bg-[var(--surface-muted)] border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-[var(--fg)]">
              Today's follow ups
            </div>
            <span className="text-[10px] px-2 py-[2px] rounded-full border border-[var(--border)] text-[var(--fg-muted)]">
              {followUps.length}
            </span>
          </div>
          <button
            className="text-xs text-[var(--accent)] hover:underline"
            onClick={() => {
              setShowDropdown(false);
              navigate(
                `${mode === "legacy" ? "/legacy" : "/websurrogate"}/notifications?scope=today`,
              );
            }}
            type="button"
          >
            Show all ->
          </button>
        </div>

        {followUps.length === 0 ? (
          <div className="p-4 text-sm text-[var(--fg-muted)]">
            No follow-ups for today.
          </div>
        ) : (
          <ul className="max-h-96 overflow-auto divide-y divide-[var(--border)]">
            {followUps.map((item) => {
              const href = buildCaseHref(item);
              const court =
                item?.petition_court_type ?? item?.petition_court ?? "";
              const label = (item.label || "").toUpperCase();
              const labelStyle =
                label === "COLD"
                  ? "ag-label-pill ag-label-pill--cold"
                  : label === "HOT"
                    ? "ag-label-pill ag-label-pill--hot"
                    : label === "WARM"
                      ? "ag-label-pill ag-label-pill--warm"
                      : "ag-label-pill ag-label-pill--na";

              const rawNote = item.note || "";
              const complex = isComplexHtml(rawNote);
              const plain = stripHtml(rawNote).trim();
              const noteLines = String(plain || "-").split(/\r?\n/);

              const firstNote =
                Array.isArray(item.notes) && item.notes.length > 0
                  ? item.notes[0]
                  : null;
              const pdfUrlRaw =
                item.pdf_url ||
                item.pdfUrl ||
                firstNote?.pdf_url ||
                firstNote?.pdfUrl ||
                null;
              const pdfHref = pdfUrlRaw ? buildPdfHref(pdfUrlRaw) : null;

              return (
                <li
                  key={item.note_id || item.case_id}
                  className="px-4 py-3 hover:bg-[var(--surface-muted)]"
                >
                  <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-[var(--fg)] truncate">
                          <span className="font-semibold">Case:</span>&nbsp;
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--accent)] underline"
                            onClick={(event) => handleCaseClick(event, href)}
                          >
                            {item.case_id}
                          </a>
                        </div>
                        <span
                          className={`inline-block text-[10px] px-2 py-[2px] rounded-full font-semibold ${labelStyle}`}
                        >
                          {label || "NA"}
                        </span>
                      </div>

                      {court && (
                        <div
                          className="text-[12px] text-[var(--fg-muted)] mt-0.5 truncate max-w-[260px]"
                          title={court}
                        >
                          <span className="font-semibold">Court:</span> {court}
                        </div>
                      )}

                      <div className="text-[12px] text-[var(--fg-muted)]">
                        <span className="font-semibold">Follow-up:</span>{" "}
                        {dayjs(item.follow_up_date).format(
                          "MMM D, YYYY, h:mm A",
                        )}
                      </div>
                      <div className="text-[12px] text-[var(--fg-muted)]">
                        <span className="font-semibold">Remark:</span>{" "}
                        {item.remark || "NA"}
                      </div>

                      <div className="text-xs text-[var(--fg)] mt-2 break-words max-w-[340px]">
                        {complex ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                showHtmlContent(rawNote);
                              }}
                              title="View full HTML"
                              className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                              type="button"
                            >
                              <FiEye
                                size={15}
                                className="text-[var(--accent)]"
                              />
                            </button>

                            {pdfHref && (
                              <a
                                href={pdfHref}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs text-[var(--accent)] underline"
                                title="Open attached PDF"
                              >
                                <FiPaperclip /> View PDF
                              </a>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="whitespace-pre-wrap">
                              {noteLines.map((line, idx) => {
                                const match = line.match(
                                  /^\s*([^:]{1,40}):\s*(.*)$/,
                                );
                                if (!match) {
                                  return (
                                    <span key={idx}>
                                      {line}
                                      {idx < noteLines.length - 1 && <br />}
                                    </span>
                                  );
                                }
                                const labelText = match[1];
                                const valueText = match[2];
                                return (
                                  <span key={idx}>
                                    <span className="font-semibold text-[var(--fg)]">
                                      {labelText}:
                                    </span>{" "}
                                    {valueText}
                                    {idx < noteLines.length - 1 && <br />}
                                  </span>
                                );
                              })}
                            </div>

                            {pdfHref && (
                              <div className="mt-1">
                                <a
                                  href={pdfHref}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 mt-1 text-xs text-[var(--accent)] underline"
                                  title="Open attached PDF"
                                >
                                  <FiPaperclip /> View PDF
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => markAsDone(item.case_id)}
                      className="text-xs shrink-0 px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                      disabled={loadingIds.includes(item.case_id)}
                      type="button"
                    >
                      {loadingIds.includes(item.case_id) ? (
                        <span className="flex items-center gap-1">
                          <FaSpinner className="animate-spin" /> Done
                        </span>
                      ) : (
                        "Done"
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>,
      document.body,
    );

  return (
    <header className="sticky top-0 z-[2000] backdrop-blur">
      <div className="w-full px-2 sm:px-4 md:px-4 lg:px-6">
        <div className="surface-card rounded-2xl px-4 py-3 md:px-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onMenuToggle}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] shadow-sm hover:-translate-y-[1px] transition"
              aria-label="Toggle sidebar"
            >
              <FiMenu className="text-lg" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--fg-muted)]">
                Web Platform
              </p>
              <h1 className="text-lg sm:text-xl font-semibold text-[var(--fg-strong)] truncate">
                {mode === "legacy"
                  ? "Legacy Workspace"
                  : mode === "acris-legacy"
                    ? "ACRIS Legacy Workspace"
                    : mode === "violation"
                      ? "Violation Workspace"
                      : mode === "violations-v2"
                        ? "Violation Workspace"
                        : "Web Surrogate"}
              </h1>
            </div>
            <span className="hidden md:inline-flex items-center px-2 py-[6px] text-[11px] font-semibold rounded-full bg-[var(--surface-muted)] text-[var(--fg-muted)] border border-[var(--border)]">
              {mode === "legacy"
                ? "Legacy"
                : mode === "acris-legacy"
                  ? "ACRIS Legacy"
                  : mode === "violation"
                    ? "Violation"
                    : mode === "violations-v2"
                      ? "Violation"
                      : "Web"}
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <ModeDropdown className="h-8 rounded-lg px-2.5 text-[11px] font-semibold" />

            <button
              type="button"
              onClick={toggleTheme}
              className="pill inline-flex items-center gap-2 h-8 px-2.5 text-[11px] font-semibold text-[var(--fg)] shadow-sm hover:-translate-y-[1px] transition rounded-lg"
            >
              {theme === "dark" ? <FiSun /> : <FiMoon />}
              <span className="hidden sm:inline">
                {theme === "dark" ? "Light" : "Dark"} mode
              </span>
            </button>

            <div className="relative">
            <button
              ref={btnRef}
              onClick={() => setShowDropdown((s) => !s)}
              className="relative h-8 w-8 rounded-lg border border-[var(--border)] bg-[var(--surface)] shadow-sm hover:-translate-y-[1px] transition flex items-center justify-center"
              title="Follow-ups"
              type="button"
            >
                <FaBell className="text-[var(--fg)] text-lg" />
                {followUps.length > 0 && (
                  <span className="absolute -top-1 -right-3 px-1.5 py-[2px] text-[8px] font-semibold rounded-full bg-red-600 text-white">
                    {followUps.length}
                  </span>
                )}
              </button>
            </div>

            <div className="flex items-center gap-2 text-sm">
              {user ? (
                <>
                  <span className="h-8 inline-flex items-center px-2.5 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[11px] font-semibold text-[var(--fg)]">
                    {user.name?.toUpperCase()}
                  </span>
                  <button
                    onClick={logout}
                    className="h-8 px-2.5 rounded-lg text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                    type="button"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <span className="text-[var(--fg-muted)]">Not signed in</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Portal dropdown */}
      {dropdownNode}

      {htmlModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
            <div className="bg-[var(--surface)] text-[var(--fg)] rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto border border-[var(--border)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Last note - full HTML</h3>
                <div>
                  <button
                    onClick={() => setHtmlModalOpen(false)}
                    className="px-2 py-1 border border-[var(--border)] rounded text-sm"
                    type="button"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div
                className="raw-html-preview prose max-w-full text-sm"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlForView(htmlModalContent),
                }}
              />

              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={async () => {
                    try {
                      await copyToClipboard(
                        stripHtml(htmlModalContent),
                        "Copied plain text",
                        "Failed to copy plain text",
                      );
                    } catch (err) {
                      console.error("Copy plain text error:", err);
                      toast.error("Failed to copy plain text");
                    }
                  }}
                  className="px-3 py-1 border border-[var(--border)] rounded text-sm"
                  type="button"
                >
                  Copy plain text
                </button>

                <button
                  onClick={async () => {
                    try {
                      await copyToClipboard(
                        htmlModalContent,
                        "Copied raw HTML",
                        "Failed to copy raw HTML",
                      );
                    } catch (err) {
                      console.error("Copy raw HTML error:", err);
                      toast.error("Failed to copy raw HTML");
                    }
                  }}
                  className="px-3 py-1 border border-[var(--border)] rounded text-sm"
                  type="button"
                >
                  Copy raw HTML
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}
